import { Teleport, defineComponent, h, onMounted, onUnmounted, ref, type PropType } from 'vue';
import { AFF_PORTAL_TARGET_ID } from './mfe-scope';
import { stampAffBoundary, unstampAffBoundary } from './aff-host';

/**
 * Teleport content into the host AFF portal sink so it inherits the Preflight
 * boundary from the `#aff-portal-root[data-aff]` ancestor — the host-side
 * counterpart to `@abax/ui`'s portal contract, for a host's OWN hand-rolled
 * body teleports (sidebar flyouts, dropdowns escaping `overflow`/`transform`).
 *
 * Replaces the `<Teleport to="body">` + literal `data-aff` pattern: a consumer
 * writes `<AffTeleport>…</AffTeleport>` and stamps no attribute. In the normal
 * path the content lands inside `#aff-portal-root`, so the boundary is inherited
 * from that ancestor and NO wrapper or per-element stamp is added — the
 * consumer's own `position: fixed` element stays a direct child of the sink,
 * with its scoped CSS and stacking unchanged.
 *
 * When the sink is absent (tests, SSR-less host, a host that did not call
 * `installAffHost`), it falls back to an owned `display: contents` boundary
 * container appended to `<body>`, so scoped Preflight still reaches the overlay.
 * The fallback container is boundary-stamped (`data-aff` only) and removed on
 * unmount; it is a layout-neutral box, so the overlay positions identically.
 *
 * The sink is resolved in `setup`, before the first render, so slotted content
 * never spends a tick under plain `<body>`. Only the owned fallback waits for
 * mount, since it appends to `<body>` itself.
 */
export const AffTeleport = defineComponent({
  name: 'AffTeleport',
  props: {
    /**
     * Explicit teleport target override. Defaults to `#aff-portal-root` with an
     * owned `<body>` boundary container as fallback when that sink is absent.
     */
    to: { type: [String, Object] as PropType<string | HTMLElement>, default: undefined },
    /** Pass-through to the underlying `<Teleport>` to render content in place. */
    disabled: { type: Boolean, default: false },
  },
  setup(props, { slots }) {
    // Content lands under [data-aff]: guarantee the boundary even if a host
    // hand-created the sink without it, then inherit it from that ancestor.
    const findSink = (): HTMLElement | null => {
      const root = document.getElementById(AFF_PORTAL_TARGET_ID);
      if (root) stampAffBoundary(root);
      return root;
    };
    const target = ref<string | HTMLElement>(props.to ?? findSink() ?? 'body');
    let ownedFallback: HTMLElement | null = null;

    onMounted(() => {
      if (target.value !== 'body') return;
      const root = findSink();
      if (root) {
        target.value = root;
        return;
      }
      // Fallback: own a layout-neutral boundary container on <body>.
      ownedFallback = document.createElement('div');
      ownedFallback.style.display = 'contents';
      stampAffBoundary(ownedFallback);
      document.body.appendChild(ownedFallback);
      target.value = ownedFallback;
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.warn(
          `[platform AffTeleport] #${AFF_PORTAL_TARGET_ID} not found; teleporting into an owned ` +
            `[data-aff] boundary container on <body>. Call installAffHost() in the host bootstrap ` +
            `so overlays share the single portal sink instead.`,
        );
      }
    });

    onUnmounted(() => {
      if (ownedFallback) {
        unstampAffBoundary(ownedFallback);
        ownedFallback.remove();
        ownedFallback = null;
      }
    });

    return () =>
      h(Teleport, { to: target.value, disabled: props.disabled }, slots.default?.() ?? []);
  },
});
